import { useEffect, useState } from "react";
import api from "../../lib/api";
import { EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Clock, CheckCircle2, Ban, CalendarClock } from "lucide-react";
import { SessionSummary } from "../expert/ExpertSessions";

export const APT_TONE = {
  CONFIRMED: "bg-emerald-100 text-emerald-800",
  PENDING_CONFIRMATION: "bg-amber-100 text-amber-800",
  RESCHEDULE_REQUESTED: "bg-amber-100 text-amber-800",
  CANCELLED: "bg-muted text-muted-foreground",
};

export function fmtIST(iso, withTime = true) {
  if (!iso) return "—";
  const opts = { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" };
  if (withTime) { opts.hour = "2-digit"; opts.minute = "2-digit"; opts.hour12 = true; }
  return new Date(iso).toLocaleString("en-IN", opts);
}

export function fmtDateLong(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
}

// Scheduling dialog: create (requestId) or reschedule (appointmentId + requestId)
export function ScheduleDialog({ open, requestId, appointmentId, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [avail, setAvail] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !requestId) return;
    setAvail(null); setDate(""); setSlots([]); setSlot(null);
    setLoading(true);
    api.get("/expert-scheduling/availability", { params: { request_id: requestId } })
      .then((r) => setAvail(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load availability"))
      .finally(() => setLoading(false));
  }, [open, requestId]);

  const pickDate = async (d) => {
    setDate(d); setSlot(null); setSlots([]);
    try {
      const r = await api.get("/expert-scheduling/slots", { params: { request_id: requestId, date: d } });
      setSlots(r.data.slots);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load slots"); }
  };

  const confirm = async () => {
    if (!slot) return;
    setBusy(true);
    try {
      if (appointmentId) {
        await api.post(`/expert-scheduling/appointments/${appointmentId}/reschedule`, { start_utc: slot.start_utc });
        toast.success("Appointment rescheduled");
      } else {
        await api.post("/expert-scheduling/appointments", { request_id: requestId, start_utc: slot.start_utc, idempotency_key: `apt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` });
        toast.success("Appointment confirmed");
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not confirm appointment"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{appointmentId ? "Reschedule Appointment" : "Schedule Consultation"}</DialogTitle>
          <DialogDescription className="sr-only">Choose an available date and time slot</DialogDescription>
        </DialogHeader>
        {loading && <p className="text-sm text-muted-foreground py-6 text-center">Loading availability…</p>}
        {avail && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Duration {avail.duration_minutes} min · times shown in {avail.timezone}</p>
            {avail.dates.length === 0 ? <EmptyState icon={CalendarDays} title="No available appointment slots" subtitle="This expert has no open slots in the booking window." /> : (
              <div>
                <Label className="mb-1.5 block text-sm">Select a date</Label>
                <div className="flex flex-wrap gap-2" data-testid="schedule-dates">
                  {avail.dates.map((d) => (
                    <button key={d.date} data-testid={`schedule-date-${d.date}`} onClick={() => pickDate(d.date)} className={`px-3 py-2 rounded-lg border text-xs ${date === d.date ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>
                      {fmtDateLong(d.date)}<span className="block text-[10px] text-muted-foreground">{d.slot_count} slots</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {date && (
              <div>
                <Label className="mb-1.5 block text-sm">Select a time</Label>
                {slots.length === 0 ? <p className="text-sm text-muted-foreground">No available appointment slots for the selected date.</p> : (
                  <div className="flex flex-wrap gap-2" data-testid="schedule-slots">
                    {slots.map((s) => (
                      <button key={s.start_utc} data-testid={`schedule-slot-${s.label}`} onClick={() => setSlot(s)} className={`px-3 py-2 rounded-lg border text-sm ${slot?.start_utc === s.start_utc ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>
                        {s.label}–{s.end_label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {slot && <div className="bg-muted rounded-lg p-3 text-sm" data-testid="schedule-review">Review: <b>{fmtDateLong(date)}</b> at <b>{slot.label}</b> (IST). No payment is collected.</div>}
          </div>
        )}
        <DialogFooter><Button onClick={confirm} disabled={!slot || busy} className="rounded-full" data-testid="schedule-confirm">{busy ? "Confirming…" : appointmentId ? "Confirm Reschedule" : "Confirm Appointment"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SocietyAppointmentsTab() {
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sched, setSched] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [sessionStates, setSessionStates] = useState({});
  const load = () => Promise.all([
    api.get("/expert-scheduling/appointments"),
    api.get("/expert-sessions/society/appointment-states"),
  ]).then(([appointments, states]) => {
    setRows(appointments.data);
    setSessionStates(Object.fromEntries(states.data.map((item) => [item.appointment_id, item])));
  }).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    try {
      await api.post(`/expert-scheduling/appointments/${detail.appointment_id}/cancel`, { reason: cancelReason });
      toast.success("Appointment cancelled"); setDetail(null); setCancelReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  if (!rows) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const upcoming = rows.filter((r) => r.status !== "CANCELLED");
  const past = rows.filter((r) => r.status === "CANCELLED");
  return (
    <div>
      {rows.length === 0 ? <EmptyState icon={CalendarClock} title="No upcoming appointments" subtitle="Schedule an accepted consultation to see it here." /> : (
        <div className="space-y-5" data-testid="society-appointments-list">
          {upcoming.length > 0 && <Section title="Upcoming & Active" rows={upcoming} onOpen={setDetail} sessionStates={sessionStates} />}
          {past.length > 0 && <Section title="Cancelled" rows={past} onOpen={setDetail} sessionStates={sessionStates} />}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && (setDetail(null), setCancelReason(""))}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="appointment-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Appointment {detail?.display_id}</DialogTitle>
            <DialogDescription className="sr-only">Appointment details and schedule history</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{detail.expert_snapshot?.name}</span>
                <span className={`text-xs rounded-full px-2.5 py-1 ${APT_TONE[detail.status]}`}>{detail.status.replace("_", " ")}</span>
              </div>
              <KV label="Service" value={detail.service_snapshot?.name} />
              <KV label="When" value={`${fmtIST(detail.scheduled_start_utc)} (IST)`} />
              <KV label="Duration" value={`${detail.duration_minutes} min`} />
              <KV label="Consultation" value={detail.request_subject} />
              {sessionStates[detail.appointment_id]?.session ? <SessionSummary session={sessionStates[detail.appointment_id].session} testId={`society-session-${detail.appointment_id}`} /> : <KV label="Session" value="Scheduled" />}
              {detail.cancellation_reason && <KV label="Cancel reason" value={detail.cancellation_reason} />}
              <div>
                <p className="text-xs text-muted-foreground mb-1 mt-2">Schedule history</p>
                <ul className="space-y-1">{(detail.history || []).map((h, i) => <li key={i} className="text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{h.action}{h.to ? ` → ${fmtIST(h.to)}` : ""} · {fmtIST(h.at)}</li>)}</ul>
              </div>
              {detail.status !== "CANCELLED" && (
                <div className="border-t pt-3 space-y-2">
                  <Button variant="outline" className="rounded-full w-full" onClick={() => { setSched({ requestId: detail.consultation_request_id, appointmentId: detail.appointment_id }); }} data-testid="appointment-reschedule-btn"><CalendarClock className="w-4 h-4 mr-1" />Reschedule</Button>
                  <Textarea data-testid="appointment-cancel-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Optional cancellation reason" />
                  <Button variant="outline" className="rounded-full w-full text-destructive" onClick={cancel} data-testid="appointment-cancel-btn"><Ban className="w-4 h-4 mr-1" />Cancel Appointment</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ScheduleDialog open={!!sched} requestId={sched?.requestId} appointmentId={sched?.appointmentId} onClose={() => setSched(null)} onDone={() => { setSched(null); setDetail(null); load(); }} />
    </div>
  );
}

function Section({ title, rows, onOpen, sessionStates }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-muted-foreground mb-2">{title}</h3>
      <div className="space-y-2">
        {rows.map((r) => (
          <button key={r.appointment_id} onClick={() => onOpen(r)} className="w-full text-left bg-card border rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap hover:border-primary/40 transition-colors" data-testid={`appointment-row-${r.appointment_id}`}>
            <div>
              <div className="font-medium">{r.expert_snapshot?.name} — {r.service_snapshot?.name}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{fmtIST(r.scheduled_start_utc)} · {r.display_id}</div>
              {sessionStates[r.appointment_id]?.session && <div className="mt-1 text-xs font-medium text-emerald-700" data-testid={`society-session-state-${r.appointment_id}`}>Consultation {sessionStates[r.appointment_id].session.status}</div>}
            </div>
            <span className={`text-xs rounded-full px-2.5 py-1 flex items-center gap-1 ${APT_TONE[r.status]}`}>{r.status === "CONFIRMED" ? <CheckCircle2 className="w-3.5 h-3.5" /> : r.status === "CANCELLED" ? <Ban className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}{r.status.replace("_", " ")}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return <div className="flex gap-2"><span className="text-muted-foreground min-w-[110px]">{label}</span><span className="font-medium">{value}</span></div>;
}
